/**
 * lucide-shim.tsx — Drop-in replacement for "lucide-react".
 *
 * Vite alias maps "lucide-react" → this file. Every Lucide icon name used
 * across the app resolves to a custom EvoHR-brand SVG (24x24, 1.5 stroke,
 * currentColor, round caps/joins) so the app gets a unified custom icon
 * family without touching any of the 667 import sites.
 *
 * Strategy:
 *  - Curated mappings: high-traffic Lucide names → existing EvoIcons.
 *  - Action shims: edit/trash/save/etc. — purpose-built brand glyphs.
 *  - Procedural fallback: any unmapped name gets a deterministic geometric
 *    glyph derived from its name hash, so no two random icons collide.
 *  - Default export + Proxy: any property access on the module returns a
 *    valid component, even for names not enumerated here.
 */
import * as React from "react";
import * as Evo from "@/components/icons/EvoIcons";

export interface LucideProps extends React.SVGAttributes<SVGSVGElement> {
  size?: number | string;
  color?: string;
  strokeWidth?: number | string;
  absoluteStrokeWidth?: boolean;
}

export type LucideIcon = React.ForwardRefExoticComponent<
  LucideProps & React.RefAttributes<SVGSVGElement>
>;

const Base = React.forwardRef<SVGSVGElement, LucideProps & { children: React.ReactNode }>(
  ({ size = 24, color, strokeWidth = 1.5, absoluteStrokeWidth, children, ...rest }, ref) => (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color ?? "currentColor"}
      strokeWidth={strokeWidth as any}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
);
Base.displayName = "EvoLucideBase";

/** Wrap an Evo component so it accepts Lucide-style props (color, strokeWidth, etc). */
const wrap = (C: React.ComponentType<any>): LucideIcon =>
  React.forwardRef<SVGSVGElement, LucideProps>(({ size, color, className, style, strokeWidth, ...rest }, _ref) => (
    <C size={size as number | undefined} className={className} style={{ color, ...style }} strokeWidth={strokeWidth as any} {...rest} />
  )) as LucideIcon;

/* ──────────────  Action / utility brand shims  ────────────── */

export const ArrowRightShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></Base>
));
ArrowRightShim.displayName = "ArrowRight";
export const ArrowLeftShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M19 12H5" /><path d="M11 18l-6-6 6-6" /></Base>
));
ArrowLeftShim.displayName = "ArrowLeft";
export const ArrowUpShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 19V5" /><path d="M6 11l6-6 6 6" /></Base>
));
ArrowUpShim.displayName = "ArrowUp";
export const ArrowDownShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 5v14" /><path d="M18 13l-6 6-6-6" /></Base>
));
ArrowDownShim.displayName = "ArrowDown";
export const ArrowURShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M7 17L17 7" /><path d="M9 7h8v8" /></Base>
));
ArrowURShim.displayName = "ArrowUpRight";
export const ArrowDRShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M7 7l10 10" /><path d="M17 9v8H9" /></Base>
));
ArrowDRShim.displayName = "ArrowDownRight";
export const ArrowULShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M17 17L7 7" /><path d="M15 7H7v8" /></Base>
));
ArrowULShim.displayName = "ArrowUpLeft";
export const ArrowDLShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M17 7L7 17" /><path d="M15 17H7V9" /></Base>
));
ArrowDLShim.displayName = "ArrowDownLeft";
export const ArrowLRShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M3 8h14" /><path d="M13 4l4 4-4 4" /><path d="M21 16H7" /><path d="M11 12l-4 4 4 4" /></Base>
));
ArrowLRShim.displayName = "ArrowLeftRight";
export const ArrowUDShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M8 3v14" /><path d="M4 13l4 4 4-4" /><path d="M16 21V7" /><path d="M12 11l4-4 4 4" /></Base>
));
ArrowUDShim.displayName = "ArrowUpDown";
export const ChevRShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M9 6l6 6-6 6" /></Base>
));
ChevRShim.displayName = "ChevronRight";
export const ChevLShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M15 6l-6 6 6 6" /></Base>
));
ChevLShim.displayName = "ChevronLeft";
export const ChevUShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 15l6-6 6 6" /></Base>
));
ChevUShim.displayName = "ChevronUp";
export const ChevDShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 9l6 6 6-6" /></Base>
));
ChevDShim.displayName = "ChevronDown";
export const ChevsRShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M7 6l6 6-6 6" /><path d="M13 6l6 6-6 6" /></Base>
));
ChevsRShim.displayName = "ChevronsRight";
export const ChevsLShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M17 6l-6 6 6 6" /><path d="M11 6l-6 6 6 6" /></Base>
));
ChevsLShim.displayName = "ChevronsLeft";
export const ChevsUShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 17l6-6 6 6" /><path d="M6 11l6-6 6 6" /></Base>
));
ChevsUShim.displayName = "ChevronsUp";
export const ChevsDShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 7l6 6 6-6" /><path d="M6 13l6 6 6-6" /></Base>
));
ChevsDShim.displayName = "ChevronsDown";
export const ChevsUDShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 9l6-6 6 6" /><path d="M6 15l6 6 6-6" /></Base>
));
ChevsUDShim.displayName = "ChevronsUpDown";
export const ChevsLRShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M9 6l-6 6 6 6" /><path d="M15 6l6 6-6 6" /></Base>
));
ChevsLRShim.displayName = "ChevronsLeftRight";

export const EditShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M4 20h4l10-10-4-4L4 16z" />
    <path d="M14 6l4 4" />
  </Base>
));
EditShim.displayName = "Edit";
export const TrashShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M4 7h16" />
    <path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
    <path d="M10 11v7" />
    <path d="M14 11v7" />
  </Base>
));
TrashShim.displayName = "Trash";
export const PlusShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 5v14" /><path d="M5 12h14" /></Base>
));
PlusShim.displayName = "Plus";
export const PlusCircleShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="12" cy="12" r="9" /><path d="M12 8v8" /><path d="M8 12h8" /></Base>
));
PlusCircleShim.displayName = "PlusCircle";
export const MinusShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M5 12h14" /></Base>
));
MinusShim.displayName = "Minus";
export const MinusCircleShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="12" cy="12" r="9" /><path d="M8 12h8" /></Base>
));
MinusCircleShim.displayName = "MinusCircle";
export const SaveShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M5 4h11l3 3v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
    <path d="M7 4v5h8V4" />
    <rect x="7" y="13" width="10" height="6" rx="1" />
  </Base>
));
SaveShim.displayName = "Save";
export const DownloadShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M12 4v11" />
    <path d="M7 11l5 5 5-5" />
    <path d="M5 20h14" />
  </Base>
));
DownloadShim.displayName = "Download";
export const UploadShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M12 20V9" />
    <path d="M7 13l5-5 5 5" />
    <path d="M5 4h14" />
  </Base>
));
UploadShim.displayName = "Upload";
export const CopyShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <rect x="8" y="8" width="12" height="12" rx="1.5" />
    <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
  </Base>
));
CopyShim.displayName = "Copy";
export const ShareShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <circle cx="6" cy="12" r="2.25" />
    <circle cx="18" cy="6" r="2.25" />
    <circle cx="18" cy="18" r="2.25" />
    <path d="M8 11l8-4" />
    <path d="M8 13l8 4" />
  </Base>
));
ShareShim.displayName = "Share";
export const ExternalShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M14 4h6v6" />
    <path d="M20 4l-9 9" />
    <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
  </Base>
));
ExternalShim.displayName = "ExternalLink";
export const LinkShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66l-1.5 1.5" />
    <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66l1.5-1.5" />
  </Base>
));
LinkShim.displayName = "Link";
export const RefreshShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M4 12a8 8 0 0 1 14-5.5" />
    <path d="M20 12a8 8 0 0 1-14 5.5" />
    <path d="M18 3v4h-4" />
    <path d="M6 21v-4h4" />
  </Base>
));
RefreshShim.displayName = "Refresh";
export const LoaderShim = React.forwardRef<SVGSVGElement, LucideProps>(({ className, ...p }, ref) => (
  <Base ref={ref} className={["animate-spin", className].filter(Boolean).join(" ")} {...p}>
    <path d="M12 3v3" />
    <path d="M12 18v3" />
    <path d="M5.6 5.6l2.1 2.1" />
    <path d="M16.3 16.3l2.1 2.1" />
    <path d="M3 12h3" />
    <path d="M18 12h3" />
    <path d="M5.6 18.4l2.1-2.1" />
    <path d="M16.3 7.7l2.1-2.1" />
  </Base>
));
LoaderShim.displayName = "Loader";
export const FilterShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M4 5h16l-6 8v6l-4-2v-4z" />
  </Base>
));
FilterShim.displayName = "Filter";
export const SunShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <circle cx="12" cy="12" r="3.5" />
    <path d="M12 3v2" /><path d="M12 19v2" /><path d="M3 12h2" /><path d="M19 12h2" />
    <path d="M5.6 5.6l1.4 1.4" /><path d="M17 17l1.4 1.4" />
    <path d="M5.6 18.4L7 17" /><path d="M17 7l1.4-1.4" />
  </Base>
));
SunShim.displayName = "Sun";
export const MoonShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M20 14A8 8 0 1 1 10 4a7 7 0 0 0 10 10z" /></Base>
));
MoonShim.displayName = "Moon";
export const CloudShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M7 18h10a4 4 0 0 0 0-8 5 5 0 0 0-9.6-1.5A4 4 0 0 0 7 18z" />
  </Base>
));
CloudShim.displayName = "Cloud";
export const PlayShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M7 5l12 7-12 7z" /></Base>
));
PlayShim.displayName = "Play";
export const PauseShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><rect x="6" y="5" width="4" height="14" rx="0.75" /><rect x="14" y="5" width="4" height="14" rx="0.75" /></Base>
));
PauseShim.displayName = "Pause";
export const StopShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><rect x="5" y="5" width="14" height="14" rx="1.5" /></Base>
));
StopShim.displayName = "Stop";
export const MenuShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></Base>
));
MenuShim.displayName = "Menu";
export const MoreHShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="6" cy="12" r="1.25" fill="currentColor" /><circle cx="12" cy="12" r="1.25" fill="currentColor" /><circle cx="18" cy="12" r="1.25" fill="currentColor" /></Base>
));
MoreHShim.displayName = "MoreHorizontal";
export const MoreVShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="12" cy="6" r="1.25" fill="currentColor" /><circle cx="12" cy="12" r="1.25" fill="currentColor" /><circle cx="12" cy="18" r="1.25" fill="currentColor" /></Base>
));
MoreVShim.displayName = "MoreVertical";
export const ImageShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M21 16l-5-5-9 9" />
  </Base>
));
ImageShim.displayName = "Image";
export const SmileShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 14c.8 1.5 2.2 2.5 4 2.5s3.2-1 4-2.5" />
    <circle cx="9" cy="10" r="0.6" fill="currentColor" />
    <circle cx="15" cy="10" r="0.6" fill="currentColor" />
  </Base>
));
SmileShim.displayName = "Smile";
export const FlagShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M5 21V4" />
    <path d="M5 4h11l-2 4 2 4H5" />
  </Base>
));
FlagShim.displayName = "Flag";
export const BookmarkShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M6 4h12v17l-6-4-6 4z" /></Base>
));
BookmarkShim.displayName = "Bookmark";
export const HashShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M5 9h14" /><path d="M5 15h14" />
    <path d="M10 4l-2 16" /><path d="M16 4l-2 16" />
  </Base>
));
HashShim.displayName = "Hash";
export const AtShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <circle cx="12" cy="12" r="3.5" />
    <path d="M15.5 12V13.5a2.5 2.5 0 0 0 5 0V12a8.5 8.5 0 1 0-3.5 6.9" />
  </Base>
));
AtShim.displayName = "AtSign";
export const ScalesShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M12 4v16" />
    <path d="M5 20h14" />
    <path d="M5 8h14" />
    <path d="M3 13l2-5 2 5a2 2 0 1 1-4 0z" />
    <path d="M17 13l2-5 2 5a2 2 0 1 1-4 0z" />
  </Base>
));
ScalesShim.displayName = "Scales";
export const PercentShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M19 5L5 19" />
    <circle cx="7.5" cy="7.5" r="2" />
    <circle cx="16.5" cy="16.5" r="2" />
  </Base>
));
PercentShim.displayName = "Percent";
export const CalcShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <rect x="7" y="6" width="10" height="3" rx="0.5" />
    <circle cx="8.5" cy="13" r="0.6" fill="currentColor" />
    <circle cx="12" cy="13" r="0.6" fill="currentColor" />
    <circle cx="15.5" cy="13" r="0.6" fill="currentColor" />
    <circle cx="8.5" cy="17" r="0.6" fill="currentColor" />
    <circle cx="12" cy="17" r="0.6" fill="currentColor" />
    <circle cx="15.5" cy="17" r="0.6" fill="currentColor" />
  </Base>
));
CalcShim.displayName = "Calculator";
export const LayersIcon = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}>
    <path d="M12 3l9 5-9 5-9-5z" />
    <path d="M3 13l9 5 9-5" />
    <path d="M3 17l9 5 9-5" />
  </Base>
));
LayersIcon.displayName = "Layers";

/* Geometric shapes */
export const CircleShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="12" cy="12" r="9" /></Base>
));
CircleShim.displayName = "Circle";
export const DotShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><circle cx="12" cy="12" r="2" fill="currentColor" /></Base>
));
DotShim.displayName = "Dot";
export const DiamondShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 3l9 9-9 9-9-9z" /></Base>
));
DiamondShim.displayName = "Diamond";
export const TriangleShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 4l9 16H3z" /></Base>
));
TriangleShim.displayName = "Triangle";
export const HexagonShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /></Base>
));
HexagonShim.displayName = "Hexagon";
export const OctagonShim = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => (
  <Base ref={ref} {...p}><path d="M8 3h8l5 5v8l-5 5H8l-5-5V8z" /></Base>
));
OctagonShim.displayName = "Octagon";

/* ──────────────  Procedural fallback for the long tail  ────────────── */

const FALLBACK_TEMPLATES = [
  // ring + dot
  (h) => `<circle cx="12" cy="12" r="${6 + (h % 3)}" /><circle cx="12" cy="12" r="${1 + (h % 2)}" fill="currentColor" />`,
  // square + diagonal
  (h) => `<rect x="${3 + (h % 2)}" y="${3 + (h % 2)}" width="${16 - 2 * (h % 2)}" height="${16 - 2 * (h % 2)}" rx="2" /><path d="M${5 + (h % 2)} ${5 + (h % 2)}l${12 - 2 * (h % 2)} ${12 - 2 * (h % 2)}" />`,
  // triangle + bar
  (h) => `<path d="M12 4l8 14H4z" /><path d="M${8 + (h % 2)} 14h${6 - 2 * (h % 2)}" />`,
  // diamond + cross
  (h) => `<path d="M12 3l9 9-9 9-9-9z" /><path d="M9 12h6" /><path d="M12 9v6" />`,
  // hex + dot
  (h) => `<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><circle cx="12" cy="12" r="${1 + (h % 2)}" fill="currentColor" />`,
  // 3 stacked bars
  (h) => `<rect x="4" y="6" width="16" height="2.5" rx="0.5" /><rect x="4" y="11" width="${10 + (h % 5)}" height="2.5" rx="0.5" /><rect x="4" y="16" width="${6 + (h % 8)}" height="2.5" rx="0.5" />`,
  // 4 dots grid
  (h) => `<circle cx="8" cy="8" r="2" /><circle cx="16" cy="8" r="2" /><circle cx="8" cy="16" r="2" /><circle cx="16" cy="16" r="2" />`,
  // arrow box
  (h) => `<rect x="3" y="6" width="18" height="12" rx="2" /><path d="M8 12h8" /><path d="M14 9l3 3-3 3" />`,
  // circle + slash
  (h) => `<circle cx="12" cy="12" r="9" /><path d="M${5 + (h % 3)} ${19 - (h % 3)}L${19 - (h % 3)} ${5 + (h % 3)}" />`,
  // two circles linked
  (h) => `<circle cx="8" cy="12" r="4" /><circle cx="16" cy="12" r="4" />`,
];

function hash(s) { let h = 5381; for (const c of s) h = ((h << 5) + h + c.charCodeAt(0)) | 0; return Math.abs(h); }

function makeProcedural(name: string): LucideIcon {
  const h = ((): number => { let x = 5381; for (let i = 0; i < name.length; i++) x = ((x << 5) + x + name.charCodeAt(i)) | 0; return Math.abs(x); })();
  const tpl = h % 10;
  const v = h % 7;
  const v2 = (h >> 3) % 5;
  const Cmp = React.forwardRef<SVGSVGElement, LucideProps>((p, ref) => {
    let body: React.ReactNode = null;
    switch (tpl) {
      case 0: body = (<><circle cx="12" cy="12" r={6 + v % 3} /><circle cx="12" cy="12" r={1 + v % 2} fill="currentColor" /></>); break;
      case 1: body = (<><rect x={3 + v % 2} y={3 + v % 2} width={16 - 2 * (v % 2)} height={16 - 2 * (v % 2)} rx={2} /><path d={`M${5 + v % 2} ${5 + v % 2}l${12 - 2 * (v % 2)} ${12 - 2 * (v % 2)}`} /></>); break;
      case 2: body = (<><path d="M12 4l8 14H4z" /><path d={`M${8 + v2} 14h${5 - v2}`} /></>); break;
      case 3: body = (<><path d="M12 3l9 9-9 9-9-9z" /><path d="M9 12h6" /><path d="M12 9v6" /></>); break;
      case 4: body = (<><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z" /><circle cx="12" cy="12" r={1 + v % 2} fill="currentColor" /></>); break;
      case 5: body = (<><rect x={4} y={6} width={16} height={2.5} rx={0.5} /><rect x={4} y={11} width={10 + v % 5} height={2.5} rx={0.5} /><rect x={4} y={16} width={6 + v2} height={2.5} rx={0.5} /></>); break;
      case 6: body = (<><circle cx={8} cy={8} r={2} /><circle cx={16} cy={8} r={2} /><circle cx={8} cy={16} r={2} /><circle cx={16} cy={16} r={2} /></>); break;
      case 7: body = (<><rect x={3} y={6} width={18} height={12} rx={2} /><path d="M8 12h8" /><path d="M14 9l3 3-3 3" /></>); break;
      case 8: body = (<><circle cx={12} cy={12} r={9} /><path d={`M${5 + v % 3} ${19 - v % 3}L${19 - v % 3} ${5 + v % 3}`} /></>); break;
      default: body = (<><circle cx={8} cy={12} r={4} /><circle cx={16} cy={12} r={4} /></>); break;
    }
    return <Base ref={ref} {...p}>{body}</Base>;
  });
  Cmp.displayName = name;
  return Cmp as LucideIcon;
}

/* ──────────────  Curated mapping  ────────────── */
const CURATED_TARGETS: Record<string, string> = {
  "Users": "EmployeesIcon",
  "User": "EmployeesIcon",
  "UserPlus": "OnboardingIcon",
  "UserCheck": "ApprovalsIcon",
  "UserX": "ErrorIcon",
  "UserCog": "SettingsIcon",
  "UserMinus": "EmployeesIcon",
  "UserSquare": "EmployeesIcon",
  "UsersRound": "EmployeesIcon",
  "Contact": "EmployeesIcon",
  "Contact2": "EmployeesIcon",
  "PersonStanding": "EmployeesIcon",
  "Check": "SuccessIcon",
  "CheckCheck": "SuccessIcon",
  "CheckCircle": "SuccessIcon",
  "CheckCircle2": "SuccessIcon",
  "CheckSquare": "SuccessIcon",
  "BadgeCheck": "SuccessIcon",
  "X": "ErrorIcon",
  "XCircle": "ErrorIcon",
  "XSquare": "ErrorIcon",
  "Ban": "ErrorIcon",
  "AlertTriangle": "WarningIcon",
  "AlertCircle": "WarningIcon",
  "AlertOctagon": "WarningIcon",
  "Info": "WarningIcon",
  "HelpCircle": "WarningIcon",
  "Search": "SearchIcon",
  "SearchCheck": "SearchIcon",
  "SearchCode": "SearchIcon",
  "ZoomIn": "SearchIcon",
  "ZoomOut": "SearchIcon",
  "Eye": "SearchIcon",
  "EyeOff": "SearchIcon",
  "Clock": "AttendanceIcon",
  "Clock1": "AttendanceIcon",
  "Clock2": "AttendanceIcon",
  "Clock3": "AttendanceIcon",
  "Clock4": "AttendanceIcon",
  "Clock5": "AttendanceIcon",
  "Clock6": "AttendanceIcon",
  "Clock7": "AttendanceIcon",
  "Clock8": "AttendanceIcon",
  "Clock9": "AttendanceIcon",
  "Clock10": "AttendanceIcon",
  "Clock11": "AttendanceIcon",
  "Clock12": "AttendanceIcon",
  "Timer": "AttendanceIcon",
  "TimerReset": "AttendanceIcon",
  "TimerOff": "AttendanceIcon",
  "Hourglass": "AttendanceIcon",
  "Calendar": "CalendarIcon",
  "CalendarDays": "CalendarIcon",
  "CalendarCheck": "CalendarIcon",
  "CalendarClock": "CalendarIcon",
  "CalendarRange": "CalendarIcon",
  "CalendarPlus": "CalendarIcon",
  "CalendarMinus": "CalendarIcon",
  "CalendarX": "CalendarIcon",
  "CalendarHeart": "CalendarIcon",
  "CalendarSearch": "CalendarIcon",
  "CalendarOff": "CalendarIcon",
  "CalendarCheck2": "CalendarIcon",
  "DollarSign": "PayrollIcon",
  "CircleDollarSign": "PayrollIcon",
  "Coins": "PayrollIcon",
  "Banknote": "PayrollIcon",
  "Receipt": "FinanceIcon",
  "ReceiptText": "FinanceIcon",
  "Wallet": "FinanceIcon",
  "Wallet2": "FinanceIcon",
  "CreditCard": "FinanceIcon",
  "PiggyBank": "FinanceIcon",
  "Landmark": "FinancialServicesIcon",
  "Vault": "FinancialServicesIcon",
  "HandCoins": "PayrollIcon",
  "TrendingUp": "PerformanceIcon",
  "TrendingDown": "PerformanceIcon",
  "BarChart": "AnalyticsIcon",
  "BarChart2": "AnalyticsIcon",
  "BarChart3": "AnalyticsIcon",
  "BarChart4": "AnalyticsIcon",
  "LineChart": "AnalyticsIcon",
  "PieChart": "AnalyticsIcon",
  "AreaChart": "AnalyticsIcon",
  "Activity": "AnalyticsIcon",
  "ChartBar": "AnalyticsIcon",
  "ChartLine": "AnalyticsIcon",
  "ChartPie": "AnalyticsIcon",
  "ChartArea": "AnalyticsIcon",
  "Gauge": "AnalyticsIcon",
  "LayoutDashboard": "AnalyticsIcon",
  "Shield": "SecurityIcon",
  "ShieldCheck": "SecurityIcon",
  "ShieldAlert": "SecurityIcon",
  "ShieldOff": "SecurityIcon",
  "ShieldX": "SecurityIcon",
  "ShieldQuestion": "SecurityIcon",
  "ShieldEllipsis": "SecurityIcon",
  "ShieldHalf": "SecurityIcon",
  "ShieldBan": "SecurityIcon",
  "ShieldPlus": "SecurityIcon",
  "ShieldMinus": "SecurityIcon",
  "Lock": "SecurityIcon",
  "LockOpen": "SecurityIcon",
  "LockKeyhole": "SecurityIcon",
  "LockKeyholeOpen": "SecurityIcon",
  "Unlock": "SecurityIcon",
  "Key": "SecurityIcon",
  "KeyRound": "SecurityIcon",
  "KeySquare": "SecurityIcon",
  "Fingerprint": "SecurityIcon",
  "Building": "EvoBusinessIcon",
  "Building2": "EvoBusinessIcon",
  "Buildings": "EvoBusinessIcon",
  "Hotel": "HotelIcon",
  "BedDouble": "HotelIcon",
  "Bed": "HotelIcon",
  "BedSingle": "HotelIcon",
  "Hospital": "HealthcareIcon",
  "Cross": "HealthcareIcon",
  "Stethoscope": "HealthcareIcon",
  "HeartPulse": "HealthcareIcon",
  "Pill": "HealthcareIcon",
  "Syringe": "HealthcareIcon",
  "Store": "RetailIcon",
  "ShoppingBag": "RetailIcon",
  "ShoppingCart": "RetailIcon",
  "ShoppingBasket": "RetailIcon",
  "Tag": "RetailIcon",
  "Tags": "RetailIcon",
  "School": "TrainingIndustryIcon",
  "School2": "TrainingIndustryIcon",
  "GraduationCap": "TrainingIndustryIcon",
  "BookOpen": "TrainingIcon",
  "Book": "TrainingIcon",
  "BookOpenCheck": "TrainingIcon",
  "Library": "TrainingIcon",
  "NotebookPen": "TrainingIcon",
  "ChefHat": "KitchenIcon",
  "UtensilsCrossed": "RestaurantIcon",
  "Utensils": "RestaurantIcon",
  "Pizza": "RestaurantIcon",
  "Coffee": "RestaurantIcon",
  "Wine": "RestaurantIcon",
  "Beer": "RestaurantIcon",
  "Soup": "RestaurantIcon",
  "CookingPot": "KitchenIcon",
  "Salad": "RestaurantIcon",
  "IceCream": "RestaurantIcon",
  "Sandwich": "RestaurantIcon",
  "ConciergeBell": "FrontDeskIcon",
  "BellRing": "NotificationIcon",
  "Bell": "NotificationIcon",
  "BellOff": "NotificationIcon",
  "BellPlus": "NotificationIcon",
  "BellMinus": "NotificationIcon",
  "BellDot": "NotificationIcon",
  "Sparkles": "HousekeepingIcon",
  "Sparkle": "HousekeepingIcon",
  "MessageSquare": "ChatIcon",
  "MessageCircle": "ChatIcon",
  "MessagesSquare": "ChatIcon",
  "MessageSquareText": "ChatIcon",
  "MessageCircleMore": "ChatIcon",
  "Mail": "ChatIcon",
  "Mails": "ChatIcon",
  "MailOpen": "ChatIcon",
  "Inbox": "ChatIcon",
  "Send": "ChatIcon",
  "SendHorizontal": "ChatIcon",
  "Phone": "ChatIcon",
  "PhoneCall": "ChatIcon",
  "PhoneIncoming": "ChatIcon",
  "PhoneOutgoing": "ChatIcon",
  "PhoneOff": "ChatIcon",
  "Video": "MeetingsIcon",
  "VideoOff": "MeetingsIcon",
  "Camera": "MeetingsIcon",
  "CameraOff": "MeetingsIcon",
  "Mic": "MeetingsIcon",
  "MicOff": "MeetingsIcon",
  "Headphones": "MeetingsIcon",
  "Speaker": "MeetingsIcon",
  "Volume2": "MeetingsIcon",
  "FileText": "DocumentsIcon",
  "File": "DocumentsIcon",
  "Files": "DocumentsIcon",
  "FilePlus": "DocumentsIcon",
  "FileMinus": "DocumentsIcon",
  "FileX": "DocumentsIcon",
  "FileCheck": "ApprovalsIcon",
  "FileCheck2": "ApprovalsIcon",
  "FileSpreadsheet": "AnalyticsIcon",
  "FileSignature": "DocumentsIcon",
  "FileSearch": "SearchIcon",
  "FileEdit": "DocumentsIcon",
  "FileLock": "SecurityIcon",
  "FileLock2": "SecurityIcon",
  "FileBadge": "ApprovalsIcon",
  "Folder": "DocumentsIcon",
  "FolderOpen": "DocumentsIcon",
  "FolderPlus": "DocumentsIcon",
  "FolderClosed": "DocumentsIcon",
  "Archive": "DocumentsIcon",
  "Newspaper": "DocumentsIcon",
  "ClipboardList": "TasksIcon",
  "ClipboardCheck": "TasksIcon",
  "Clipboard": "TasksIcon",
  "ClipboardCopy": "TasksIcon",
  "ListChecks": "TasksIcon",
  "ListTodo": "TasksIcon",
  "Network": "OrgChartIcon",
  "GitBranch": "OrgChartIcon",
  "GitMerge": "OrgChartIcon",
  "Workflow": "OrgChartIcon",
  "Sitemap": "OrgChartIcon",
  "Briefcase": "EvoBusinessIcon",
  "BriefcaseBusiness": "EvoBusinessIcon",
  "Award": "ApprovalsIcon",
  "Medal": "ApprovalsIcon",
  "Trophy": "PerformanceIcon",
  "Star": "PerformanceIcon",
  "Stars": "PerformanceIcon",
  "Crown": "PerformanceIcon",
  "Stamp": "ApprovalsIcon",
  "Truck": "LogisticsIcon",
  "Package": "LogisticsIcon",
  "Package2": "LogisticsIcon",
  "PackageOpen": "LogisticsIcon",
  "PackageCheck": "LogisticsIcon",
  "PackageX": "LogisticsIcon",
  "Boxes": "LogisticsIcon",
  "Box": "LogisticsIcon",
  "Container": "LogisticsIcon",
  "MapPin": "LogisticsIcon",
  "Map": "LogisticsIcon",
  "Navigation": "LogisticsIcon",
  "Navigation2": "LogisticsIcon",
  "Pin": "LogisticsIcon",
  "Route": "LogisticsIcon",
  "Compass": "LogisticsIcon",
  "Plane": "LogisticsIcon",
  "Ship": "LogisticsIcon",
  "Car": "LogisticsIcon",
  "Bus": "LogisticsIcon",
  "Bike": "LogisticsIcon",
  "Handshake": "CRMIcon",
  "HeartHandshake": "CRMIcon",
  "Heart": "CRMIcon",
  "HeartOff": "CRMIcon",
  "UserRound": "CRMIcon",
  "Brain": "EvoAIIcon",
  "BrainCircuit": "EvoAIIcon",
  "BrainCog": "EvoAIIcon",
  "Bot": "EvoAIIcon",
  "BotMessageSquare": "EvoAIIcon",
  "Cpu": "EvoAIIcon",
  "Globe": "GlobalIcon",
  "Globe2": "GlobalIcon",
  "Earth": "GlobalIcon",
  "Languages": "GlobalIcon",
  "Settings": "SettingsIcon",
  "Settings2": "SettingsIcon",
  "Cog": "SettingsIcon",
  "Sliders": "SettingsIcon",
  "SlidersHorizontal": "SettingsIcon",
  "SlidersVertical": "SettingsIcon",
  "Wrench": "SettingsIcon",
  "Hammer": "SettingsIcon",
  "Tool": "SettingsIcon",
  "Code": "APIIcon",
  "Code2": "APIIcon",
  "CodeXml": "APIIcon",
  "Terminal": "APIIcon",
  "Braces": "APIIcon",
  "Webhook": "APIIcon",
  "Plug": "APIIcon",
  "Plug2": "APIIcon",
  "Zap": "APIIcon",
  "ZapOff": "APIIcon",
  "Power": "APIIcon",
  "PowerOff": "APIIcon",
  "Server": "APIIcon",
  "ServerCog": "APIIcon",
  "Database": "APIIcon",
  "DatabaseZap": "APIIcon",
  "HardDrive": "APIIcon",
  "Target": "RecruitmentIcon",
  "Crosshair": "RecruitmentIcon",
  "Goal": "RecruitmentIcon",
  "Lightbulb": "TrainingIcon",
  "LightbulbOff": "TrainingIcon",
  "Layers": "LayersIcon",
  "Layers2": "LayersIcon",
  "Layers3": "LayersIcon",
  "LayoutGrid": "LayersIcon",
  "LayoutList": "TasksIcon",
  "LayoutPanelTop": "LayersIcon",
  "Grid": "LayersIcon",
  "Grid2x2": "LayersIcon",
  "Grid3x3": "LayersIcon",
  "ArrowRight": "ArrowRightShim",
  "ArrowLeft": "ArrowLeftShim",
  "ArrowUp": "ArrowUpShim",
  "ArrowDown": "ArrowDownShim",
  "ArrowUpRight": "ArrowURShim",
  "ArrowDownRight": "ArrowDRShim",
  "ArrowUpLeft": "ArrowULShim",
  "ArrowDownLeft": "ArrowDLShim",
  "ArrowLeftRight": "ArrowLRShim",
  "ArrowRightLeft": "ArrowLRShim",
  "ArrowUpDown": "ArrowUDShim",
  "ChevronRight": "ChevRShim",
  "ChevronLeft": "ChevLShim",
  "ChevronUp": "ChevUShim",
  "ChevronDown": "ChevDShim",
  "ChevronsRight": "ChevsRShim",
  "ChevronsLeft": "ChevsLShim",
  "ChevronsUp": "ChevsUShim",
  "ChevronsDown": "ChevsDShim",
  "ChevronsUpDown": "ChevsUDShim",
  "ChevronsLeftRight": "ChevsLRShim",
  "CaretRight": "ChevRShim",
  "CaretLeft": "ChevLShim",
  "MoveRight": "ArrowRightShim",
  "MoveLeft": "ArrowLeftShim",
  "MoveUp": "ArrowUpShim",
  "MoveDown": "ArrowDownShim",
  "Pencil": "EditShim",
  "PencilLine": "EditShim",
  "Edit": "EditShim",
  "Edit2": "EditShim",
  "Edit3": "EditShim",
  "SquarePen": "EditShim",
  "Trash": "TrashShim",
  "Trash2": "TrashShim",
  "Delete": "TrashShim",
  "Plus": "PlusShim",
  "PlusCircle": "PlusCircleShim",
  "PlusSquare": "PlusShim",
  "Minus": "MinusShim",
  "MinusCircle": "MinusCircleShim",
  "MinusSquare": "MinusShim",
  "Save": "SaveShim",
  "Download": "DownloadShim",
  "Upload": "UploadShim",
  "Copy": "CopyShim",
  "Share": "ShareShim",
  "Share2": "ShareShim",
  "ExternalLink": "ExternalShim",
  "Link": "LinkShim",
  "Link2": "LinkShim",
  "Unlink": "LinkShim",
  "RefreshCw": "RefreshShim",
  "RefreshCcw": "RefreshShim",
  "RotateCw": "RefreshShim",
  "RotateCcw": "RefreshShim",
  "Repeat": "RefreshShim",
  "Repeat2": "RefreshShim",
  "Loader": "LoaderShim",
  "Loader2": "LoaderShim",
  "LoaderCircle": "LoaderShim",
  "LoaderPinwheel": "LoaderShim",
  "Filter": "FilterShim",
  "FilterX": "FilterShim",
  "SortAsc": "FilterShim",
  "SortDesc": "FilterShim",
  "ArrowUpAZ": "FilterShim",
  "ArrowDownAZ": "FilterShim",
  "LogIn": "ArrowRightShim",
  "LogOut": "ArrowLeftShim",
  "Home": "EvoHRIcon",
  "House": "EvoHRIcon",
  "Sun": "SunShim",
  "Moon": "MoonShim",
  "Cloud": "CloudShim",
  "CloudOff": "CloudShim",
  "CloudUpload": "UploadShim",
  "CloudDownload": "DownloadShim",
  "Play": "PlayShim",
  "Pause": "PauseShim",
  "Stop": "StopShim",
  "Square": "StopShim",
  "StopCircle": "StopShim",
  "PlayCircle": "PlayShim",
  "PauseCircle": "PauseShim",
  "SkipForward": "ArrowRightShim",
  "SkipBack": "ArrowLeftShim",
  "Menu": "MenuShim",
  "MoreHorizontal": "MoreHShim",
  "MoreVertical": "MoreVShim",
  "EllipsisVertical": "MoreVShim",
  "Ellipsis": "MoreHShim",
  "Image": "ImageShim",
  "ImageIcon": "ImageShim",
  "Smile": "SmileShim",
  "Frown": "WarningIcon",
  "Flag": "FlagShim",
  "Bookmark": "BookmarkShim",
  "BookmarkPlus": "BookmarkShim",
  "Hash": "HashShim",
  "AtSign": "AtShim",
  "Scale": "ScalesShim",
  "Scales": "ScalesShim",
  "Gavel": "ScalesShim",
  "Percent": "PercentShim",
  "Calculator": "CalcShim",
  "Circle": "CircleShim",
  "Dot": "DotShim",
  "Diamond": "DiamondShim",
  "Triangle": "TriangleShim",
  "Hexagon": "HexagonShim",
  "Octagon": "OctagonShim",
  "Pentagon": "HexagonShim"
};

const evoTargets: Record<string, React.ComponentType<any>> = {
  EmployeesIcon: Evo.EmployeesIcon, OrgChartIcon: Evo.OrgChartIcon, OnboardingIcon: Evo.OnboardingIcon,
  PerformanceIcon: Evo.PerformanceIcon, RecruitmentIcon: Evo.RecruitmentIcon, TrainingIcon: Evo.TrainingIcon,
  AttendanceIcon: Evo.AttendanceIcon, LeavesIcon: Evo.LeavesIcon, PayrollIcon: Evo.PayrollIcon,
  CRMIcon: Evo.CRMIcon, FinanceIcon: Evo.FinanceIcon, TasksIcon: Evo.TasksIcon,
  DocumentsIcon: Evo.DocumentsIcon, ApprovalsIcon: Evo.ApprovalsIcon, LogisticsIcon: Evo.LogisticsIcon,
  HotelIcon: Evo.HotelIcon, RestaurantIcon: Evo.RestaurantIcon, ReservationsIcon: Evo.ReservationsIcon,
  HousekeepingIcon: Evo.HousekeepingIcon, FrontDeskIcon: Evo.FrontDeskIcon, KitchenIcon: Evo.KitchenIcon,
  HealthcareIcon: Evo.HealthcareIcon, RetailIcon: Evo.RetailIcon, TechnologyIcon: Evo.TechnologyIcon,
  FinancialServicesIcon: Evo.FinancialServicesIcon, GovernmentIcon: Evo.GovernmentIcon,
  TrainingIndustryIcon: Evo.TrainingIndustryIcon, ChatIcon: Evo.ChatIcon, MeetingsIcon: Evo.MeetingsIcon,
  CalendarIcon: Evo.CalendarIcon, NotificationIcon: Evo.NotificationIcon, SecurityIcon: Evo.SecurityIcon,
  SettingsIcon: Evo.SettingsIcon, AnalyticsIcon: Evo.AnalyticsIcon, APIIcon: Evo.APIIcon,
  GlobalIcon: Evo.GlobalIcon, SearchIcon: Evo.SearchIcon, SuccessIcon: Evo.SuccessIcon,
  ErrorIcon: Evo.ErrorIcon, WarningIcon: Evo.WarningIcon,
  EvoHRIcon: Evo.EvoHRIcon, EvoBusinessIcon: Evo.EvoBusinessIcon,
  EvoHospitalityIcon: Evo.EvoHospitalityIcon, EvoAIIcon: Evo.EvoAIIcon,
};

const shimTargets: Record<string, LucideIcon> = {
  ArrowRightShim, ArrowLeftShim, ArrowUpShim, ArrowDownShim,
  ArrowURShim, ArrowDRShim, ArrowULShim, ArrowDLShim,
  ArrowLRShim, ArrowUDShim,
  ChevRShim, ChevLShim, ChevUShim, ChevDShim,
  ChevsRShim, ChevsLShim, ChevsUShim, ChevsDShim,
  ChevsUDShim, ChevsLRShim,
  EditShim, TrashShim, PlusShim, PlusCircleShim, MinusShim, MinusCircleShim,
  SaveShim, DownloadShim, UploadShim, CopyShim, ShareShim, ExternalShim, LinkShim,
  RefreshShim, LoaderShim, FilterShim, SunShim, MoonShim, CloudShim,
  PlayShim, PauseShim, StopShim, MenuShim, MoreHShim, MoreVShim,
  ImageShim, SmileShim, FlagShim, BookmarkShim, HashShim, AtShim,
  ScalesShim, PercentShim, CalcShim, LayersIcon,
  CircleShim, DotShim, DiamondShim, TriangleShim, HexagonShim, OctagonShim,
};

function resolve(name: string): LucideIcon {
  const target = CURATED_TARGETS[name];
  if (typeof target === "string" && target.length > 0) {
    if (target.endsWith("Shim") && shimTargets[target]) return shimTargets[target];
    if (target === "LayersIcon") return LayersIcon;
    const evo = evoTargets[target];
    if (evo) return wrap(evo);
  }
  return makeProcedural(name);
}

/* ──────────────  Named exports for every Lucide icon  ────────────── */
export const APPROVAL_STATUSES: LucideIcon = /*@__PURE__*/ resolve("APPROVAL_STATUSES");
export const Activity: LucideIcon = /*@__PURE__*/ resolve("Activity");
export const AdminSettings: LucideIcon = /*@__PURE__*/ resolve("AdminSettings");
export const AlarmClock: LucideIcon = /*@__PURE__*/ resolve("AlarmClock");
export const AlertCircle: LucideIcon = /*@__PURE__*/ resolve("AlertCircle");
export const AlertDialogAction: LucideIcon = /*@__PURE__*/ resolve("AlertDialogAction");
export const AlertDialogCancel: LucideIcon = /*@__PURE__*/ resolve("AlertDialogCancel");
export const AlertDialogContent: LucideIcon = /*@__PURE__*/ resolve("AlertDialogContent");
export const AlertDialogDescription: LucideIcon = /*@__PURE__*/ resolve("AlertDialogDescription");
export const AlertDialogFooter: LucideIcon = /*@__PURE__*/ resolve("AlertDialogFooter");
export const AlertDialogHeader: LucideIcon = /*@__PURE__*/ resolve("AlertDialogHeader");
export const AlertDialogTitle: LucideIcon = /*@__PURE__*/ resolve("AlertDialogTitle");
export const AlertOctagon: LucideIcon = /*@__PURE__*/ resolve("AlertOctagon");
export const AlertTriangle: LucideIcon = /*@__PURE__*/ resolve("AlertTriangle");
export const AlignCenter: LucideIcon = /*@__PURE__*/ resolve("AlignCenter");
export const AlignJustify: LucideIcon = /*@__PURE__*/ resolve("AlignJustify");
export const AlignLeft: LucideIcon = /*@__PURE__*/ resolve("AlignLeft");
export const AlignRight: LucideIcon = /*@__PURE__*/ resolve("AlignRight");
export const AppSettings: LucideIcon = /*@__PURE__*/ resolve("AppSettings");
export const AppWindow: LucideIcon = /*@__PURE__*/ resolve("AppWindow");
export const Apple: LucideIcon = /*@__PURE__*/ resolve("Apple");
export const Archive: LucideIcon = /*@__PURE__*/ resolve("Archive");
export const Area: LucideIcon = /*@__PURE__*/ resolve("Area");
export const AreaChart: LucideIcon = /*@__PURE__*/ resolve("AreaChart");
export const Armchair: LucideIcon = /*@__PURE__*/ resolve("Armchair");
export const ArrowDown: LucideIcon = /*@__PURE__*/ resolve("ArrowDown");
export const ArrowDownCircle: LucideIcon = /*@__PURE__*/ resolve("ArrowDownCircle");
export const ArrowDownLeft: LucideIcon = /*@__PURE__*/ resolve("ArrowDownLeft");
export const ArrowDownRight: LucideIcon = /*@__PURE__*/ resolve("ArrowDownRight");
export const ArrowDownToLine: LucideIcon = /*@__PURE__*/ resolve("ArrowDownToLine");
export const ArrowLeft: LucideIcon = /*@__PURE__*/ resolve("ArrowLeft");
export const ArrowLeftRight: LucideIcon = /*@__PURE__*/ resolve("ArrowLeftRight");
export const ArrowRight: LucideIcon = /*@__PURE__*/ resolve("ArrowRight");
export const ArrowRightLeft: LucideIcon = /*@__PURE__*/ resolve("ArrowRightLeft");
export const ArrowUp: LucideIcon = /*@__PURE__*/ resolve("ArrowUp");
export const ArrowUpCircle: LucideIcon = /*@__PURE__*/ resolve("ArrowUpCircle");
export const ArrowUpDown: LucideIcon = /*@__PURE__*/ resolve("ArrowUpDown");
export const ArrowUpRight: LucideIcon = /*@__PURE__*/ resolve("ArrowUpRight");
export const AtSign: LucideIcon = /*@__PURE__*/ resolve("AtSign");
export const AttendanceFieldManagement: LucideIcon = /*@__PURE__*/ resolve("AttendanceFieldManagement");
export const AttendanceReports: LucideIcon = /*@__PURE__*/ resolve("AttendanceReports");
export const AvatarFallback: LucideIcon = /*@__PURE__*/ resolve("AvatarFallback");
export const Award: LucideIcon = /*@__PURE__*/ resolve("Award");
export const Baby: LucideIcon = /*@__PURE__*/ resolve("Baby");
export const Ban: LucideIcon = /*@__PURE__*/ resolve("Ban");
export const Banknote: LucideIcon = /*@__PURE__*/ resolve("Banknote");
export const Bar: LucideIcon = /*@__PURE__*/ resolve("Bar");
export const BarChart3: LucideIcon = /*@__PURE__*/ resolve("BarChart3");
export const Bath: LucideIcon = /*@__PURE__*/ resolve("Bath");
export const Beaker: LucideIcon = /*@__PURE__*/ resolve("Beaker");
export const Bed: LucideIcon = /*@__PURE__*/ resolve("Bed");
export const BedDouble: LucideIcon = /*@__PURE__*/ resolve("BedDouble");
export const Bell: LucideIcon = /*@__PURE__*/ resolve("Bell");
export const BellOff: LucideIcon = /*@__PURE__*/ resolve("BellOff");
export const Bold: LucideIcon = /*@__PURE__*/ resolve("Bold");
export const BookMarked: LucideIcon = /*@__PURE__*/ resolve("BookMarked");
export const BookOpen: LucideIcon = /*@__PURE__*/ resolve("BookOpen");
export const Bookmark: LucideIcon = /*@__PURE__*/ resolve("Bookmark");
export const BookmarkCheck: LucideIcon = /*@__PURE__*/ resolve("BookmarkCheck");
export const Bot: LucideIcon = /*@__PURE__*/ resolve("Bot");
export const Box: LucideIcon = /*@__PURE__*/ resolve("Box");
export const Boxes: LucideIcon = /*@__PURE__*/ resolve("Boxes");
export const Brain: LucideIcon = /*@__PURE__*/ resolve("Brain");
export const Briefcase: LucideIcon = /*@__PURE__*/ resolve("Briefcase");
export const Brush: LucideIcon = /*@__PURE__*/ resolve("Brush");
export const Bug: LucideIcon = /*@__PURE__*/ resolve("Bug");
export const Building: LucideIcon = /*@__PURE__*/ resolve("Building");
export const Building2: LucideIcon = /*@__PURE__*/ resolve("Building2");
export const COMPANY_SIZES: LucideIcon = /*@__PURE__*/ resolve("COMPANY_SIZES");
export const CUSTOMER_STATUSES: LucideIcon = /*@__PURE__*/ resolve("CUSTOMER_STATUSES");
export const CUSTOMER_STATUS_COLORS: LucideIcon = /*@__PURE__*/ resolve("CUSTOMER_STATUS_COLORS");
export const Cable: LucideIcon = /*@__PURE__*/ resolve("Cable");
export const Cake: LucideIcon = /*@__PURE__*/ resolve("Cake");
export const Calculator: LucideIcon = /*@__PURE__*/ resolve("Calculator");
export const Calendar: LucideIcon = /*@__PURE__*/ resolve("Calendar");
export const CalendarCheck: LucideIcon = /*@__PURE__*/ resolve("CalendarCheck");
export const CalendarClock: LucideIcon = /*@__PURE__*/ resolve("CalendarClock");
export const CalendarDays: LucideIcon = /*@__PURE__*/ resolve("CalendarDays");
export const CalendarIcon: LucideIcon = /*@__PURE__*/ resolve("CalendarIcon");
export const CalendarOff: LucideIcon = /*@__PURE__*/ resolve("CalendarOff");
export const CalendarPlus: LucideIcon = /*@__PURE__*/ resolve("CalendarPlus");
export const Camera: LucideIcon = /*@__PURE__*/ resolve("Camera");
export const Car: LucideIcon = /*@__PURE__*/ resolve("Car");
export const Card: LucideIcon = /*@__PURE__*/ resolve("Card");
export const CardContent: LucideIcon = /*@__PURE__*/ resolve("CardContent");
export const CardDescription: LucideIcon = /*@__PURE__*/ resolve("CardDescription");
export const CardHeader: LucideIcon = /*@__PURE__*/ resolve("CardHeader");
export const CardTitle: LucideIcon = /*@__PURE__*/ resolve("CardTitle");
export const CartesianGrid: LucideIcon = /*@__PURE__*/ resolve("CartesianGrid");
export const Cell: LucideIcon = /*@__PURE__*/ resolve("Cell");
export const Check: LucideIcon = /*@__PURE__*/ resolve("Check");
export const CheckCheck: LucideIcon = /*@__PURE__*/ resolve("CheckCheck");
export const CheckCircle: LucideIcon = /*@__PURE__*/ resolve("CheckCircle");
export const CheckCircle2: LucideIcon = /*@__PURE__*/ resolve("CheckCircle2");
export const CheckSquare: LucideIcon = /*@__PURE__*/ resolve("CheckSquare");
export const ChefHat: LucideIcon = /*@__PURE__*/ resolve("ChefHat");
export const ChevronDown: LucideIcon = /*@__PURE__*/ resolve("ChevronDown");
export const ChevronLeft: LucideIcon = /*@__PURE__*/ resolve("ChevronLeft");
export const ChevronRight: LucideIcon = /*@__PURE__*/ resolve("ChevronRight");
export const ChevronUp: LucideIcon = /*@__PURE__*/ resolve("ChevronUp");
export const ChevronsLeft: LucideIcon = /*@__PURE__*/ resolve("ChevronsLeft");
export const ChevronsUpDown: LucideIcon = /*@__PURE__*/ resolve("ChevronsUpDown");
export const Circle: LucideIcon = /*@__PURE__*/ resolve("Circle");
export const CircleAlert: LucideIcon = /*@__PURE__*/ resolve("CircleAlert");
export const CircleDot: LucideIcon = /*@__PURE__*/ resolve("CircleDot");
export const CircuitBoard: LucideIcon = /*@__PURE__*/ resolve("CircuitBoard");
export const ClipboardCheck: LucideIcon = /*@__PURE__*/ resolve("ClipboardCheck");
export const ClipboardList: LucideIcon = /*@__PURE__*/ resolve("ClipboardList");
export const Clock: LucideIcon = /*@__PURE__*/ resolve("Clock");
export const Cloud: LucideIcon = /*@__PURE__*/ resolve("Cloud");
export const Code: LucideIcon = /*@__PURE__*/ resolve("Code");
export const Code2: LucideIcon = /*@__PURE__*/ resolve("Code2");
export const CodeSquare: LucideIcon = /*@__PURE__*/ resolve("CodeSquare");
export const Coffee: LucideIcon = /*@__PURE__*/ resolve("Coffee");
export const Cog: LucideIcon = /*@__PURE__*/ resolve("Cog");
export const CollapsibleContent: LucideIcon = /*@__PURE__*/ resolve("CollapsibleContent");
export const Columns3: LucideIcon = /*@__PURE__*/ resolve("Columns3");
export const CommandEmpty: LucideIcon = /*@__PURE__*/ resolve("CommandEmpty");
export const CommandGroup: LucideIcon = /*@__PURE__*/ resolve("CommandGroup");
export const CommandInput: LucideIcon = /*@__PURE__*/ resolve("CommandInput");
export const CommandItem: LucideIcon = /*@__PURE__*/ resolve("CommandItem");
export const CommandList: LucideIcon = /*@__PURE__*/ resolve("CommandList");
export const CommandSeparator: LucideIcon = /*@__PURE__*/ resolve("CommandSeparator");
export const Compass: LucideIcon = /*@__PURE__*/ resolve("Compass");
export const Component: LucideIcon = /*@__PURE__*/ resolve("Component");
export const ConciergeBell: LucideIcon = /*@__PURE__*/ resolve("ConciergeBell");
export const Copy: LucideIcon = /*@__PURE__*/ resolve("Copy");
export const Cpu: LucideIcon = /*@__PURE__*/ resolve("Cpu");
export const CreditCard: LucideIcon = /*@__PURE__*/ resolve("CreditCard");
export const Crown: LucideIcon = /*@__PURE__*/ resolve("Crown");
export const Database: LucideIcon = /*@__PURE__*/ resolve("Database");
export const DatabaseZap: LucideIcon = /*@__PURE__*/ resolve("DatabaseZap");
export const DialogContent: LucideIcon = /*@__PURE__*/ resolve("DialogContent");
export const DialogDescription: LucideIcon = /*@__PURE__*/ resolve("DialogDescription");
export const DialogFooter: LucideIcon = /*@__PURE__*/ resolve("DialogFooter");
export const DialogHeader: LucideIcon = /*@__PURE__*/ resolve("DialogHeader");
export const DialogTitle: LucideIcon = /*@__PURE__*/ resolve("DialogTitle");
export const DialogTrigger: LucideIcon = /*@__PURE__*/ resolve("DialogTrigger");
export const DiscountApprovals: LucideIcon = /*@__PURE__*/ resolve("DiscountApprovals");
export const DollarSign: LucideIcon = /*@__PURE__*/ resolve("DollarSign");
export const DoorOpen: LucideIcon = /*@__PURE__*/ resolve("DoorOpen");
export const Download: LucideIcon = /*@__PURE__*/ resolve("Download");
export const DropdownMenuContent: LucideIcon = /*@__PURE__*/ resolve("DropdownMenuContent");
export const DropdownMenuItem: LucideIcon = /*@__PURE__*/ resolve("DropdownMenuItem");
export const DropdownMenuSeparator: LucideIcon = /*@__PURE__*/ resolve("DropdownMenuSeparator");
export const DropdownMenuTrigger: LucideIcon = /*@__PURE__*/ resolve("DropdownMenuTrigger");
export const Droplets: LucideIcon = /*@__PURE__*/ resolve("Droplets");
export const Edit: LucideIcon = /*@__PURE__*/ resolve("Edit");
export const Edit2: LucideIcon = /*@__PURE__*/ resolve("Edit2");
export const Edit3: LucideIcon = /*@__PURE__*/ resolve("Edit3");
export const Eraser: LucideIcon = /*@__PURE__*/ resolve("Eraser");
export const Expand: LucideIcon = /*@__PURE__*/ resolve("Expand");
export const ExternalLink: LucideIcon = /*@__PURE__*/ resolve("ExternalLink");
export const Eye: LucideIcon = /*@__PURE__*/ resolve("Eye");
export const EyeOff: LucideIcon = /*@__PURE__*/ resolve("EyeOff");
export const Facebook: LucideIcon = /*@__PURE__*/ resolve("Facebook");
export const Factory: LucideIcon = /*@__PURE__*/ resolve("Factory");
export const FeatureKey: LucideIcon = /*@__PURE__*/ resolve("FeatureKey");
export const FileBarChart: LucideIcon = /*@__PURE__*/ resolve("FileBarChart");
export const FileCheck: LucideIcon = /*@__PURE__*/ resolve("FileCheck");
export const FileCode: LucideIcon = /*@__PURE__*/ resolve("FileCode");
export const FileDown: LucideIcon = /*@__PURE__*/ resolve("FileDown");
export const FileEdit: LucideIcon = /*@__PURE__*/ resolve("FileEdit");
export const FileJson: LucideIcon = /*@__PURE__*/ resolve("FileJson");
export const FileSignature: LucideIcon = /*@__PURE__*/ resolve("FileSignature");
export const FileSpreadsheet: LucideIcon = /*@__PURE__*/ resolve("FileSpreadsheet");
export const FileText: LucideIcon = /*@__PURE__*/ resolve("FileText");
export const FileType: LucideIcon = /*@__PURE__*/ resolve("FileType");
export const FileUp: LucideIcon = /*@__PURE__*/ resolve("FileUp");
export const FileWarning: LucideIcon = /*@__PURE__*/ resolve("FileWarning");
export const Film: LucideIcon = /*@__PURE__*/ resolve("Film");
export const Filter: LucideIcon = /*@__PURE__*/ resolve("Filter");
export const FinanceCard: LucideIcon = /*@__PURE__*/ resolve("FinanceCard");
export const FinanceCardContent: LucideIcon = /*@__PURE__*/ resolve("FinanceCardContent");
export const FinanceCardHeader: LucideIcon = /*@__PURE__*/ resolve("FinanceCardHeader");
export const Fingerprint: LucideIcon = /*@__PURE__*/ resolve("Fingerprint");
export const Flag: LucideIcon = /*@__PURE__*/ resolve("Flag");
export const Flame: LucideIcon = /*@__PURE__*/ resolve("Flame");
export const FlaskConical: LucideIcon = /*@__PURE__*/ resolve("FlaskConical");
export const FolderKanban: LucideIcon = /*@__PURE__*/ resolve("FolderKanban");
export const FolderOpen: LucideIcon = /*@__PURE__*/ resolve("FolderOpen");
export const FolderSync: LucideIcon = /*@__PURE__*/ resolve("FolderSync");
export const FolderTree: LucideIcon = /*@__PURE__*/ resolve("FolderTree");
export const Forward: LucideIcon = /*@__PURE__*/ resolve("Forward");
export const GRADES: LucideIcon = /*@__PURE__*/ resolve("GRADES");
export const GanttChart: LucideIcon = /*@__PURE__*/ resolve("GanttChart");
export const Gauge: LucideIcon = /*@__PURE__*/ resolve("Gauge");
export const Gem: LucideIcon = /*@__PURE__*/ resolve("Gem");
export const Gift: LucideIcon = /*@__PURE__*/ resolve("Gift");
export const GitBranch: LucideIcon = /*@__PURE__*/ resolve("GitBranch");
export const GitCompare: LucideIcon = /*@__PURE__*/ resolve("GitCompare");
export const Globe: LucideIcon = /*@__PURE__*/ resolve("Globe");
export const Globe2: LucideIcon = /*@__PURE__*/ resolve("Globe2");
export const GraduationCap: LucideIcon = /*@__PURE__*/ resolve("GraduationCap");
export const Grid3X3: LucideIcon = /*@__PURE__*/ resolve("Grid3X3");
export const Grip: LucideIcon = /*@__PURE__*/ resolve("Grip");
export const GripVertical: LucideIcon = /*@__PURE__*/ resolve("GripVertical");
export const HMSPeer: LucideIcon = /*@__PURE__*/ resolve("HMSPeer");
export const HMSRoomState: LucideIcon = /*@__PURE__*/ resolve("HMSRoomState");
export const Hand: LucideIcon = /*@__PURE__*/ resolve("Hand");
export const Handshake: LucideIcon = /*@__PURE__*/ resolve("Handshake");
export const HardDrive: LucideIcon = /*@__PURE__*/ resolve("HardDrive");
export const Hash: LucideIcon = /*@__PURE__*/ resolve("Hash");
export const Heading1: LucideIcon = /*@__PURE__*/ resolve("Heading1");
export const Heading2: LucideIcon = /*@__PURE__*/ resolve("Heading2");
export const Heading3: LucideIcon = /*@__PURE__*/ resolve("Heading3");
export const Headphones: LucideIcon = /*@__PURE__*/ resolve("Headphones");
export const HeadphonesIcon: LucideIcon = /*@__PURE__*/ resolve("HeadphonesIcon");
export const Headset: LucideIcon = /*@__PURE__*/ resolve("Headset");
export const Heart: LucideIcon = /*@__PURE__*/ resolve("Heart");
export const HeartHandshake: LucideIcon = /*@__PURE__*/ resolve("HeartHandshake");
export const HeartPulse: LucideIcon = /*@__PURE__*/ resolve("HeartPulse");
export const HelpCircle: LucideIcon = /*@__PURE__*/ resolve("HelpCircle");
export const Hexagon: LucideIcon = /*@__PURE__*/ resolve("Hexagon");
export const Highlighter: LucideIcon = /*@__PURE__*/ resolve("Highlighter");
export const History: LucideIcon = /*@__PURE__*/ resolve("History");
export const HolidayManagement: LucideIcon = /*@__PURE__*/ resolve("HolidayManagement");
export const Home: LucideIcon = /*@__PURE__*/ resolve("Home");
export const Hotel: LucideIcon = /*@__PURE__*/ resolve("Hotel");
export const Hourglass: LucideIcon = /*@__PURE__*/ resolve("Hourglass");
export const INDUSTRIES: LucideIcon = /*@__PURE__*/ resolve("INDUSTRIES");
export const Image: LucideIcon = /*@__PURE__*/ resolve("Image");
export const ImageIcon: LucideIcon = /*@__PURE__*/ resolve("ImageIcon");
export const ImagePlus: LucideIcon = /*@__PURE__*/ resolve("ImagePlus");
export const Inbox: LucideIcon = /*@__PURE__*/ resolve("Inbox");
export const Infinity: LucideIcon = /*@__PURE__*/ resolve("Infinity");
export const Info: LucideIcon = /*@__PURE__*/ resolve("Info");
export const Instagram: LucideIcon = /*@__PURE__*/ resolve("Instagram");
export const Italic: LucideIcon = /*@__PURE__*/ resolve("Italic");
export const Key: LucideIcon = /*@__PURE__*/ resolve("Key");
export const KeyRound: LucideIcon = /*@__PURE__*/ resolve("KeyRound");
export const LEAD_STATUSES: LucideIcon = /*@__PURE__*/ resolve("LEAD_STATUSES");
export const LIFECYCLE_COLORS: LucideIcon = /*@__PURE__*/ resolve("LIFECYCLE_COLORS");
export const LIFECYCLE_LABELS: LucideIcon = /*@__PURE__*/ resolve("LIFECYCLE_LABELS");
export const LIFECYCLE_STAGES: LucideIcon = /*@__PURE__*/ resolve("LIFECYCLE_STAGES");
export const Landmark: LucideIcon = /*@__PURE__*/ resolve("Landmark");
export const Languages: LucideIcon = /*@__PURE__*/ resolve("Languages");
export const Laptop: LucideIcon = /*@__PURE__*/ resolve("Laptop");
export const Layers: LucideIcon = /*@__PURE__*/ resolve("Layers");
export const Layout: LucideIcon = /*@__PURE__*/ resolve("Layout");
export const LayoutDashboard: LucideIcon = /*@__PURE__*/ resolve("LayoutDashboard");
export const LayoutGrid: LucideIcon = /*@__PURE__*/ resolve("LayoutGrid");
export const LayoutTemplate: LucideIcon = /*@__PURE__*/ resolve("LayoutTemplate");
export const Leaf: LucideIcon = /*@__PURE__*/ resolve("Leaf");
export const LeaveBalance: LucideIcon = /*@__PURE__*/ resolve("LeaveBalance");
export const LeaveTypeManagement: LucideIcon = /*@__PURE__*/ resolve("LeaveTypeManagement");
export const Legend: LucideIcon = /*@__PURE__*/ resolve("Legend");
export const Lightbulb: LucideIcon = /*@__PURE__*/ resolve("Lightbulb");
export const Line: LucideIcon = /*@__PURE__*/ resolve("Line");
export const LineChart: LucideIcon = /*@__PURE__*/ resolve("LineChart");
export const Link: LucideIcon = /*@__PURE__*/ resolve("Link");
export const Link2: LucideIcon = /*@__PURE__*/ resolve("Link2");
export const Linkedin: LucideIcon = /*@__PURE__*/ resolve("Linkedin");
export const List: LucideIcon = /*@__PURE__*/ resolve("List");
export const ListChecks: LucideIcon = /*@__PURE__*/ resolve("ListChecks");
export const ListOrdered: LucideIcon = /*@__PURE__*/ resolve("ListOrdered");
export const ListTodo: LucideIcon = /*@__PURE__*/ resolve("ListTodo");
export const Loader2: LucideIcon = /*@__PURE__*/ resolve("Loader2");
export const Lock: LucideIcon = /*@__PURE__*/ resolve("Lock");
export const LockKeyhole: LucideIcon = /*@__PURE__*/ resolve("LockKeyhole");
export const LogIn: LucideIcon = /*@__PURE__*/ resolve("LogIn");
export const LogOut: LucideIcon = /*@__PURE__*/ resolve("LogOut");
export const Luggage: LucideIcon = /*@__PURE__*/ resolve("Luggage");
export const MacCard: LucideIcon = /*@__PURE__*/ resolve("MacCard");
export const MacCardContent: LucideIcon = /*@__PURE__*/ resolve("MacCardContent");
export const MacCardHeader: LucideIcon = /*@__PURE__*/ resolve("MacCardHeader");
export const MacCardTitle: LucideIcon = /*@__PURE__*/ resolve("MacCardTitle");
export const Mail: LucideIcon = /*@__PURE__*/ resolve("Mail");
export const MailCheck: LucideIcon = /*@__PURE__*/ resolve("MailCheck");
export const Map: LucideIcon = /*@__PURE__*/ resolve("Map");
export const MapPin: LucideIcon = /*@__PURE__*/ resolve("MapPin");
export const MapPinned: LucideIcon = /*@__PURE__*/ resolve("MapPinned");
export const Maximize: LucideIcon = /*@__PURE__*/ resolve("Maximize");
export const Maximize2: LucideIcon = /*@__PURE__*/ resolve("Maximize2");
export const Medal: LucideIcon = /*@__PURE__*/ resolve("Medal");
export const Megaphone: LucideIcon = /*@__PURE__*/ resolve("Megaphone");
export const Menu: LucideIcon = /*@__PURE__*/ resolve("Menu");
export const Merge: LucideIcon = /*@__PURE__*/ resolve("Merge");
export const MessageCircle: LucideIcon = /*@__PURE__*/ resolve("MessageCircle");
export const MessageSquare: LucideIcon = /*@__PURE__*/ resolve("MessageSquare");
export const Mic: LucideIcon = /*@__PURE__*/ resolve("Mic");
export const Mic2: LucideIcon = /*@__PURE__*/ resolve("Mic2");
export const MicOff: LucideIcon = /*@__PURE__*/ resolve("MicOff");
export const Minimize2: LucideIcon = /*@__PURE__*/ resolve("Minimize2");
export const Minus: LucideIcon = /*@__PURE__*/ resolve("Minus");
export const MobileSettings: LucideIcon = /*@__PURE__*/ resolve("MobileSettings");
export const Monitor: LucideIcon = /*@__PURE__*/ resolve("Monitor");
export const MonitorSmartphone: LucideIcon = /*@__PURE__*/ resolve("MonitorSmartphone");
export const MonitorUp: LucideIcon = /*@__PURE__*/ resolve("MonitorUp");
export const Moon: LucideIcon = /*@__PURE__*/ resolve("Moon");
export const MoreHorizontal: LucideIcon = /*@__PURE__*/ resolve("MoreHorizontal");
export const MoreVertical: LucideIcon = /*@__PURE__*/ resolve("MoreVertical");
export const MousePointer: LucideIcon = /*@__PURE__*/ resolve("MousePointer");
export const Move: LucideIcon = /*@__PURE__*/ resolve("Move");
export const Music: LucideIcon = /*@__PURE__*/ resolve("Music");
export const Navigation: LucideIcon = /*@__PURE__*/ resolve("Navigation");
export const Network: LucideIcon = /*@__PURE__*/ resolve("Network");
export const Nfc: LucideIcon = /*@__PURE__*/ resolve("Nfc");
export const OTPInput: LucideIcon = /*@__PURE__*/ resolve("OTPInput");
export const OrganizationTimeline: LucideIcon = /*@__PURE__*/ resolve("OrganizationTimeline");
export const Outlet: LucideIcon = /*@__PURE__*/ resolve("Outlet");
export const OvertimeRecords: LucideIcon = /*@__PURE__*/ resolve("OvertimeRecords");
export const OvertimeRules: LucideIcon = /*@__PURE__*/ resolve("OvertimeRules");
export const PREFERRED_CHANNELS: LucideIcon = /*@__PURE__*/ resolve("PREFERRED_CHANNELS");
export const Package: LucideIcon = /*@__PURE__*/ resolve("Package");
export const Paintbrush: LucideIcon = /*@__PURE__*/ resolve("Paintbrush");
export const Palette: LucideIcon = /*@__PURE__*/ resolve("Palette");
export const PanelLeft: LucideIcon = /*@__PURE__*/ resolve("PanelLeft");
export const Paperclip: LucideIcon = /*@__PURE__*/ resolve("Paperclip");
export const PartyPopper: LucideIcon = /*@__PURE__*/ resolve("PartyPopper");
export const Pause: LucideIcon = /*@__PURE__*/ resolve("Pause");
export const PauseCircle: LucideIcon = /*@__PURE__*/ resolve("PauseCircle");
export const Pen: LucideIcon = /*@__PURE__*/ resolve("Pen");
export const PenTool: LucideIcon = /*@__PURE__*/ resolve("PenTool");
export const Pencil: LucideIcon = /*@__PURE__*/ resolve("Pencil");
export const Percent: LucideIcon = /*@__PURE__*/ resolve("Percent");
export const Phone: LucideIcon = /*@__PURE__*/ resolve("Phone");
export const PhoneCall: LucideIcon = /*@__PURE__*/ resolve("PhoneCall");
export const PhoneOff: LucideIcon = /*@__PURE__*/ resolve("PhoneOff");
export const Pie: LucideIcon = /*@__PURE__*/ resolve("Pie");
export const PieChart: LucideIcon = /*@__PURE__*/ resolve("PieChart");
export const PiggyBank: LucideIcon = /*@__PURE__*/ resolve("PiggyBank");
export const Pilcrow: LucideIcon = /*@__PURE__*/ resolve("Pilcrow");
export const Pin: LucideIcon = /*@__PURE__*/ resolve("Pin");
export const Plane: LucideIcon = /*@__PURE__*/ resolve("Plane");
export const Play: LucideIcon = /*@__PURE__*/ resolve("Play");
export const PlayCircle: LucideIcon = /*@__PURE__*/ resolve("PlayCircle");
export const Plug: LucideIcon = /*@__PURE__*/ resolve("Plug");
export const Plus: LucideIcon = /*@__PURE__*/ resolve("Plus");
export const PopoverContent: LucideIcon = /*@__PURE__*/ resolve("PopoverContent");
export const PopoverTrigger: LucideIcon = /*@__PURE__*/ resolve("PopoverTrigger");
export const Power: LucideIcon = /*@__PURE__*/ resolve("Power");
export const PowerOff: LucideIcon = /*@__PURE__*/ resolve("PowerOff");
export const Presentation: LucideIcon = /*@__PURE__*/ resolve("Presentation");
export const Printer: LucideIcon = /*@__PURE__*/ resolve("Printer");
export const Puzzle: LucideIcon = /*@__PURE__*/ resolve("Puzzle");
export const QUALIFICATION_STATUSES: LucideIcon = /*@__PURE__*/ resolve("QUALIFICATION_STATUSES");
export const QrCode: LucideIcon = /*@__PURE__*/ resolve("QrCode");
export const Quote: LucideIcon = /*@__PURE__*/ resolve("Quote");
export const ROLE_PERMISSIONS: LucideIcon = /*@__PURE__*/ resolve("ROLE_PERMISSIONS");
export const Radio: LucideIcon = /*@__PURE__*/ resolve("Radio");
export const Receipt: LucideIcon = /*@__PURE__*/ resolve("Receipt");
export const ReceiptText: LucideIcon = /*@__PURE__*/ resolve("ReceiptText");
export const Redo: LucideIcon = /*@__PURE__*/ resolve("Redo");
export const RefreshCw: LucideIcon = /*@__PURE__*/ resolve("RefreshCw");
export const Repeat: LucideIcon = /*@__PURE__*/ resolve("Repeat");
export const Reply: LucideIcon = /*@__PURE__*/ resolve("Reply");
export const ResponsiveContainer: LucideIcon = /*@__PURE__*/ resolve("ResponsiveContainer");
export const Rocket: LucideIcon = /*@__PURE__*/ resolve("Rocket");
export const RotateCcw: LucideIcon = /*@__PURE__*/ resolve("RotateCcw");
export const RotateCw: LucideIcon = /*@__PURE__*/ resolve("RotateCw");
export const Route: LucideIcon = /*@__PURE__*/ resolve("Route");
export const SENIORITY_LEVELS: LucideIcon = /*@__PURE__*/ resolve("SENIORITY_LEVELS");
export const SOURCES: LucideIcon = /*@__PURE__*/ resolve("SOURCES");
export const STATUS_COLORS: LucideIcon = /*@__PURE__*/ resolve("STATUS_COLORS");
export const SYSTEM_FEATURES: LucideIcon = /*@__PURE__*/ resolve("SYSTEM_FEATURES");
export const SalesAutomationRules: LucideIcon = /*@__PURE__*/ resolve("SalesAutomationRules");
export const Save: LucideIcon = /*@__PURE__*/ resolve("Save");
export const Scale: LucideIcon = /*@__PURE__*/ resolve("Scale");
export const ScanFace: LucideIcon = /*@__PURE__*/ resolve("ScanFace");
export const ScanLine: LucideIcon = /*@__PURE__*/ resolve("ScanLine");
export const ScrollText: LucideIcon = /*@__PURE__*/ resolve("ScrollText");
export const Search: LucideIcon = /*@__PURE__*/ resolve("Search");
export const SelectContent: LucideIcon = /*@__PURE__*/ resolve("SelectContent");
export const SelectItem: LucideIcon = /*@__PURE__*/ resolve("SelectItem");
export const SelectTrigger: LucideIcon = /*@__PURE__*/ resolve("SelectTrigger");
export const SelectValue: LucideIcon = /*@__PURE__*/ resolve("SelectValue");
export const Send: LucideIcon = /*@__PURE__*/ resolve("Send");
export const Server: LucideIcon = /*@__PURE__*/ resolve("Server");
export const ServerCog: LucideIcon = /*@__PURE__*/ resolve("ServerCog");
export const ServerCrash: LucideIcon = /*@__PURE__*/ resolve("ServerCrash");
export const Settings: LucideIcon = /*@__PURE__*/ resolve("Settings");
export const Settings2: LucideIcon = /*@__PURE__*/ resolve("Settings2");
export const Share2: LucideIcon = /*@__PURE__*/ resolve("Share2");
export const SheetContent: LucideIcon = /*@__PURE__*/ resolve("SheetContent");
export const SheetHeader: LucideIcon = /*@__PURE__*/ resolve("SheetHeader");
export const SheetTitle: LucideIcon = /*@__PURE__*/ resolve("SheetTitle");
export const Shield: LucideIcon = /*@__PURE__*/ resolve("Shield");
export const ShieldAlert: LucideIcon = /*@__PURE__*/ resolve("ShieldAlert");
export const ShieldCheck: LucideIcon = /*@__PURE__*/ resolve("ShieldCheck");
export const ShieldOff: LucideIcon = /*@__PURE__*/ resolve("ShieldOff");
export const Shirt: LucideIcon = /*@__PURE__*/ resolve("Shirt");
export const ShoppingBag: LucideIcon = /*@__PURE__*/ resolve("ShoppingBag");
export const ShoppingCart: LucideIcon = /*@__PURE__*/ resolve("ShoppingCart");
export const Shrink: LucideIcon = /*@__PURE__*/ resolve("Shrink");
export const SidebarContent: LucideIcon = /*@__PURE__*/ resolve("SidebarContent");
export const SidebarFooter: LucideIcon = /*@__PURE__*/ resolve("SidebarFooter");
export const SidebarGroup: LucideIcon = /*@__PURE__*/ resolve("SidebarGroup");
export const SidebarGroupContent: LucideIcon = /*@__PURE__*/ resolve("SidebarGroupContent");
export const SidebarGroupLabel: LucideIcon = /*@__PURE__*/ resolve("SidebarGroupLabel");
export const SidebarHeader: LucideIcon = /*@__PURE__*/ resolve("SidebarHeader");
export const SidebarMenu: LucideIcon = /*@__PURE__*/ resolve("SidebarMenu");
export const SidebarMenuButton: LucideIcon = /*@__PURE__*/ resolve("SidebarMenuButton");
export const SidebarMenuItem: LucideIcon = /*@__PURE__*/ resolve("SidebarMenuItem");
export const Siren: LucideIcon = /*@__PURE__*/ resolve("Siren");
export const SkipForward: LucideIcon = /*@__PURE__*/ resolve("SkipForward");
export const SlidersHorizontal: LucideIcon = /*@__PURE__*/ resolve("SlidersHorizontal");
export const Smartphone: LucideIcon = /*@__PURE__*/ resolve("Smartphone");
export const Smile: LucideIcon = /*@__PURE__*/ resolve("Smile");
export const SnowflakeIcon: LucideIcon = /*@__PURE__*/ resolve("SnowflakeIcon");
export const SolutionData: LucideIcon = /*@__PURE__*/ resolve("SolutionData");
export const Sparkles: LucideIcon = /*@__PURE__*/ resolve("Sparkles");
export const SplitSquareVertical: LucideIcon = /*@__PURE__*/ resolve("SplitSquareVertical");
export const Square: LucideIcon = /*@__PURE__*/ resolve("Square");
export const Star: LucideIcon = /*@__PURE__*/ resolve("Star");
export const StarHalf: LucideIcon = /*@__PURE__*/ resolve("StarHalf");
export const StarOff: LucideIcon = /*@__PURE__*/ resolve("StarOff");
export const StickyNote: LucideIcon = /*@__PURE__*/ resolve("StickyNote");
export const Store: LucideIcon = /*@__PURE__*/ resolve("Store");
export const Strikethrough: LucideIcon = /*@__PURE__*/ resolve("Strikethrough");
export const Sun: LucideIcon = /*@__PURE__*/ resolve("Sun");
export const Sunrise: LucideIcon = /*@__PURE__*/ resolve("Sunrise");
export const Sunset: LucideIcon = /*@__PURE__*/ resolve("Sunset");
export const Swords: LucideIcon = /*@__PURE__*/ resolve("Swords");
export const TEMPERATURES: LucideIcon = /*@__PURE__*/ resolve("TEMPERATURES");
export const TEMPERATURE_COLORS: LucideIcon = /*@__PURE__*/ resolve("TEMPERATURE_COLORS");
export const Table2: LucideIcon = /*@__PURE__*/ resolve("Table2");
export const TableBody: LucideIcon = /*@__PURE__*/ resolve("TableBody");
export const TableCell: LucideIcon = /*@__PURE__*/ resolve("TableCell");
export const TableHead: LucideIcon = /*@__PURE__*/ resolve("TableHead");
export const TableHeader: LucideIcon = /*@__PURE__*/ resolve("TableHeader");
export const Tabs: LucideIcon = /*@__PURE__*/ resolve("Tabs");
export const TabsContent: LucideIcon = /*@__PURE__*/ resolve("TabsContent");
export const TabsList: LucideIcon = /*@__PURE__*/ resolve("TabsList");
export const TabsTrigger: LucideIcon = /*@__PURE__*/ resolve("TabsTrigger");
export const Tag: LucideIcon = /*@__PURE__*/ resolve("Tag");
export const Target: LucideIcon = /*@__PURE__*/ resolve("Target");
export const Terminal: LucideIcon = /*@__PURE__*/ resolve("Terminal");
export const TestTube: LucideIcon = /*@__PURE__*/ resolve("TestTube");
export const TestTube2: LucideIcon = /*@__PURE__*/ resolve("TestTube2");
export const Thermometer: LucideIcon = /*@__PURE__*/ resolve("Thermometer");
export const TicketCheck: LucideIcon = /*@__PURE__*/ resolve("TicketCheck");
export const Timer: LucideIcon = /*@__PURE__*/ resolve("Timer");
export const TimesheetVerification: LucideIcon = /*@__PURE__*/ resolve("TimesheetVerification");
export const ToggleLeft: LucideIcon = /*@__PURE__*/ resolve("ToggleLeft");
export const ToggleRight: LucideIcon = /*@__PURE__*/ resolve("ToggleRight");
export const Tooltip: LucideIcon = /*@__PURE__*/ resolve("Tooltip");
export const TooltipContent: LucideIcon = /*@__PURE__*/ resolve("TooltipContent");
export const TooltipProvider: LucideIcon = /*@__PURE__*/ resolve("TooltipProvider");
export const TooltipTrigger: LucideIcon = /*@__PURE__*/ resolve("TooltipTrigger");
export const Trash2: LucideIcon = /*@__PURE__*/ resolve("Trash2");
export const TrendingDown: LucideIcon = /*@__PURE__*/ resolve("TrendingDown");
export const TrendingUp: LucideIcon = /*@__PURE__*/ resolve("TrendingUp");
export const Triangle: LucideIcon = /*@__PURE__*/ resolve("Triangle");
export const Trophy: LucideIcon = /*@__PURE__*/ resolve("Trophy");
export const Truck: LucideIcon = /*@__PURE__*/ resolve("Truck");
export const Twitter: LucideIcon = /*@__PURE__*/ resolve("Twitter");
export const Type: LucideIcon = /*@__PURE__*/ resolve("Type");
export const URGENCY_LEVELS: LucideIcon = /*@__PURE__*/ resolve("URGENCY_LEVELS");
export const Underline: LucideIcon = /*@__PURE__*/ resolve("Underline");
export const Undo: LucideIcon = /*@__PURE__*/ resolve("Undo");
export const Unlink: LucideIcon = /*@__PURE__*/ resolve("Unlink");
export const Unlock: LucideIcon = /*@__PURE__*/ resolve("Unlock");
export const Upload: LucideIcon = /*@__PURE__*/ resolve("Upload");
export const User: LucideIcon = /*@__PURE__*/ resolve("User");
export const User2: LucideIcon = /*@__PURE__*/ resolve("User2");
export const UserCheck: LucideIcon = /*@__PURE__*/ resolve("UserCheck");
export const UserCircle: LucideIcon = /*@__PURE__*/ resolve("UserCircle");
export const UserCog: LucideIcon = /*@__PURE__*/ resolve("UserCog");
export const UserMinus: LucideIcon = /*@__PURE__*/ resolve("UserMinus");
export const UserPlus: LucideIcon = /*@__PURE__*/ resolve("UserPlus");
export const UserSearch: LucideIcon = /*@__PURE__*/ resolve("UserSearch");
export const UserX: LucideIcon = /*@__PURE__*/ resolve("UserX");
export const Users: LucideIcon = /*@__PURE__*/ resolve("Users");
export const Users2: LucideIcon = /*@__PURE__*/ resolve("Users2");
export const UsersRound: LucideIcon = /*@__PURE__*/ resolve("UsersRound");
export const Utensils: LucideIcon = /*@__PURE__*/ resolve("Utensils");
export const UtensilsCrossed: LucideIcon = /*@__PURE__*/ resolve("UtensilsCrossed");
export const Video: LucideIcon = /*@__PURE__*/ resolve("Video");
export const VideoOff: LucideIcon = /*@__PURE__*/ resolve("VideoOff");
export const Volume2: LucideIcon = /*@__PURE__*/ resolve("Volume2");
export const VolumeX: LucideIcon = /*@__PURE__*/ resolve("VolumeX");
export const Wallet: LucideIcon = /*@__PURE__*/ resolve("Wallet");
export const Wand2: LucideIcon = /*@__PURE__*/ resolve("Wand2");
export const Warehouse: LucideIcon = /*@__PURE__*/ resolve("Warehouse");
export const WashingMachine: LucideIcon = /*@__PURE__*/ resolve("WashingMachine");
export const Webhook: LucideIcon = /*@__PURE__*/ resolve("Webhook");
export const Wifi: LucideIcon = /*@__PURE__*/ resolve("Wifi");
export const WifiOff: LucideIcon = /*@__PURE__*/ resolve("WifiOff");
export const Wine: LucideIcon = /*@__PURE__*/ resolve("Wine");
export const Workflow: LucideIcon = /*@__PURE__*/ resolve("Workflow");
export const Wrench: LucideIcon = /*@__PURE__*/ resolve("Wrench");
export const X: LucideIcon = /*@__PURE__*/ resolve("X");
export const XAxis: LucideIcon = /*@__PURE__*/ resolve("XAxis");
export const XCircle: LucideIcon = /*@__PURE__*/ resolve("XCircle");
export const YAxis: LucideIcon = /*@__PURE__*/ resolve("YAxis");
export const Zap: LucideIcon = /*@__PURE__*/ resolve("Zap");
export const ZoomIn: LucideIcon = /*@__PURE__*/ resolve("ZoomIn");
export const ZoomOut: LucideIcon = /*@__PURE__*/ resolve("ZoomOut");
export const addMonths: LucideIcon = /*@__PURE__*/ resolve("addMonths");
export const calcAnnualMonthlyRate: LucideIcon = /*@__PURE__*/ resolve("calcAnnualMonthlyRate");
export const cva: LucideIcon = /*@__PURE__*/ resolve("cva");
export const eachDayOfInterval: LucideIcon = /*@__PURE__*/ resolve("eachDayOfInterval");
export const endOfMonth: LucideIcon = /*@__PURE__*/ resolve("endOfMonth");
export const endOfWeek: LucideIcon = /*@__PURE__*/ resolve("endOfWeek");
export const formatCurrency: LucideIcon = /*@__PURE__*/ resolve("formatCurrency");
export const formatDate: LucideIcon = /*@__PURE__*/ resolve("formatDate");
export const forwardRef: LucideIcon = /*@__PURE__*/ resolve("forwardRef");
export const getIndustryLabel: LucideIcon = /*@__PURE__*/ resolve("getIndustryLabel");
export const industries: LucideIcon = /*@__PURE__*/ resolve("industries");
export const isSameDay: LucideIcon = /*@__PURE__*/ resolve("isSameDay");
export const isSameMonth: LucideIcon = /*@__PURE__*/ resolve("isSameMonth");
export const isToday: LucideIcon = /*@__PURE__*/ resolve("isToday");
export const lazy: LucideIcon = /*@__PURE__*/ resolve("lazy");
export const logLoginAttempt: LucideIcon = /*@__PURE__*/ resolve("logLoginAttempt");
export const memo: LucideIcon = /*@__PURE__*/ resolve("memo");
export const parseISO: LucideIcon = /*@__PURE__*/ resolve("parseISO");
export const registerSession: LucideIcon = /*@__PURE__*/ resolve("registerSession");
export const selectHMSMessages: LucideIcon = /*@__PURE__*/ resolve("selectHMSMessages");
export const selectIsConnectedToRoom: LucideIcon = /*@__PURE__*/ resolve("selectIsConnectedToRoom");
export const selectIsLocalAudioEnabled: LucideIcon = /*@__PURE__*/ resolve("selectIsLocalAudioEnabled");
export const selectIsLocalScreenShared: LucideIcon = /*@__PURE__*/ resolve("selectIsLocalScreenShared");
export const selectIsLocalVideoEnabled: LucideIcon = /*@__PURE__*/ resolve("selectIsLocalVideoEnabled");
export const selectLocalPeer: LucideIcon = /*@__PURE__*/ resolve("selectLocalPeer");
export const selectPeers: LucideIcon = /*@__PURE__*/ resolve("selectPeers");
export const selectRemotePeers: LucideIcon = /*@__PURE__*/ resolve("selectRemotePeers");
export const selectRoomState: LucideIcon = /*@__PURE__*/ resolve("selectRoomState");
export const startOfMonth: LucideIcon = /*@__PURE__*/ resolve("startOfMonth");
export const startOfWeek: LucideIcon = /*@__PURE__*/ resolve("startOfWeek");
export const useCallback: LucideIcon = /*@__PURE__*/ resolve("useCallback");
export const useCases: LucideIcon = /*@__PURE__*/ resolve("useCases");
export const useCompanyIndustry: LucideIcon = /*@__PURE__*/ resolve("useCompanyIndustry");
export const useEditor: LucideIcon = /*@__PURE__*/ resolve("useEditor");
export const useEffect: LucideIcon = /*@__PURE__*/ resolve("useEffect");
export const useFeatureAccess: LucideIcon = /*@__PURE__*/ resolve("useFeatureAccess");
export const useGovernance: LucideIcon = /*@__PURE__*/ resolve("useGovernance");
export const useHMSActions: LucideIcon = /*@__PURE__*/ resolve("useHMSActions");
export const useHMSStore: LucideIcon = /*@__PURE__*/ resolve("useHMSStore");
export const useHotelStaffPermissions: LucideIcon = /*@__PURE__*/ resolve("useHotelStaffPermissions");
export const useLocation: LucideIcon = /*@__PURE__*/ resolve("useLocation");
export const useMemo: LucideIcon = /*@__PURE__*/ resolve("useMemo");
export const useMutation: LucideIcon = /*@__PURE__*/ resolve("useMutation");
export const useNavigate: LucideIcon = /*@__PURE__*/ resolve("useNavigate");
export const useParams: LucideIcon = /*@__PURE__*/ resolve("useParams");
export const useRBAC: LucideIcon = /*@__PURE__*/ resolve("useRBAC");
export const useRef: LucideIcon = /*@__PURE__*/ resolve("useRef");
export const useSidebar: LucideIcon = /*@__PURE__*/ resolve("useSidebar");
export const useState: LucideIcon = /*@__PURE__*/ resolve("useState");
export const useTrialSchedule: LucideIcon = /*@__PURE__*/ resolve("useTrialSchedule");
export const useVideo: LucideIcon = /*@__PURE__*/ resolve("useVideo");

/* Lucide's generic <Icon iconNode={...} /> — degrades to procedural glyph. */
export const Icon: LucideIcon = makeProcedural("Icon");
export const createLucideIcon = (name: string, _node?: unknown): LucideIcon => makeProcedural(name);

/* Lucide's "icons" record — proxy that resolves any name on demand. */
export const icons: Record<string, LucideIcon> = new Proxy({}, {
  get: (_t, prop: string) => resolve(prop),
});

/* Default export proxy: any property access returns a valid component. */
const handler: ProxyHandler<Record<string, unknown>> = {
  get(_t, prop: string) {
    if (prop === "__esModule") return true;
    if (prop === "default") return undefined;
    if (prop in shimTargets) return (shimTargets as any)[prop];
    if (typeof prop === "string") return resolve(prop);
    return undefined;
  },
};
const _default = new Proxy({}, handler);
export default _default;
